<template>
  <van-popup
    v-model:show="visible"
    position="bottom"
    :close-on-click-overlay="false"
    class="physical-second-check-popup"
    @closed="handleClosed"
  >
    <section class="second-check-dialog">
      <header class="second-check-dialog__head">
        <div data-strong>{{ title || t("card.identityVerification") }}</div>
        <button type="button" @click="visible = false">
          <van-icon name="cross" size="16" />
        </button>
      </header>

      <div class="second-check-dialog__form">
        <section class="second-check-section">
          <label>{{ t("card.imageVerification") }}</label>
          <div class="second-check-captcha-row">
            <van-field
              v-model="imageVerificationCode"
              :placeholder="t('auth.enterVerificationCode')"
              clearable
            />
            <button
              type="button"
              class="second-check-captcha"
              :disabled="captchaLoading"
              @click="loadCardCaptcha"
            >
              <app-image v-if="captchaImage" :src="captchaImage" alt="" />
            </button>
          </div>
        </section>

        <section class="second-check-section second-check-section--sms">
          <div class="second-check-section__title">
            <label>{{ t("card.phoneVerification") }}</label>
            <p v-if="phone">
              {{ t("card.secondCheckSmsSentTo") }}<em>{{ phone }}</em>
            </p>
          </div>
          <van-field
            v-model="smsVerificationCode"
            :placeholder="t('auth.enterVerificationCode')"
            clearable
          >
            <template #button>
              <button
                type="button"
                class="second-check-sms-button"
                :disabled="smsSending || smsCountdown > 0"
                @click="sendSecondCheckSms"
              >
                {{
                  smsCountdown > 0
                    ? `${t("auth.resend")}${smsCountdown}s`
                    : t("auth.getCode")
                }}
              </button>
            </template>
          </van-field>
        </section>
      </div>

      <van-button
        block
        round
        :loading="confirmLoading"
        :disabled="!canConfirm"
        class="second-check-dialog__confirm"
        @click="confirm"
      >
        {{ t("common.confirm") }}
      </van-button>
    </section>
  </van-popup>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { showFailToast } from "vant";
import { useI18n } from "vue-i18n";
import { pcCardCaptcha, pcCardSmsSend } from "@/api/card";

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  confirmLoading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: "",
  },
  phone: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["update:show", "confirm", "sms-sent", "closed"]);
const { t } = useI18n();

const visible = computed({
  get: () => props.show,
  set: (value) => emit("update:show", value),
});
const captchaImage = ref("");
const imageVerificationCode = ref("");
const smsVerificationCode = ref("");
const captchaLoading = ref(false);
const smsSending = ref(false);
const smsCountdown = ref(0);
let smsCountdownTimer = null;

const canConfirm = computed(() =>
  Boolean(String(smsVerificationCode.value || "").trim()),
);

function resolveCaptchaImage(result) {
  const value = result?.data ?? result;
  if (typeof value === "string") {
    return value;
  }
  return String(value?.url || value?.image || "").trim();
}

function startSmsCountdown() {
  smsCountdown.value = 60;
  window.clearInterval(smsCountdownTimer);
  smsCountdownTimer = window.setInterval(() => {
    smsCountdown.value -= 1;
    if (smsCountdown.value <= 0) {
      window.clearInterval(smsCountdownTimer);
      smsCountdownTimer = null;
    }
  }, 1000);
}

async function loadCardCaptcha() {
  captchaLoading.value = true;
  imageVerificationCode.value = "";
  try {
    const result = await pcCardCaptcha({ meta: { showLoading: false } });
    captchaImage.value = resolveCaptchaImage(result);
    if (!captchaImage.value) {
      showFailToast(t("common.retryLater"));
    }
  } finally {
    captchaLoading.value = false;
  }
}

async function sendSecondCheckSms() {
  const imgCode = String(imageVerificationCode.value || "").trim();
  if (!imgCode) {
    showFailToast(t("auth.enterVerificationCode"));
    return;
  }

  smsSending.value = true;
  try {
    await pcCardSmsSend({ imgCode }, { meta: { showLoading: false } });
    startSmsCountdown();
    emit("sms-sent");
  } finally {
    smsSending.value = false;
  }
}

function confirm() {
  if (!canConfirm.value || props.confirmLoading) {
    return;
  }
  emit("confirm", {
    smsCode: String(smsVerificationCode.value || "").trim(),
  });
}

function reset() {
  window.clearInterval(smsCountdownTimer);
  smsCountdownTimer = null;
  captchaImage.value = "";
  imageVerificationCode.value = "";
  smsVerificationCode.value = "";
  smsCountdown.value = 0;
}

function handleClosed() {
  reset();
  emit("closed");
}

watch(
  () => props.show,
  (value) => {
    if (value) {
      loadCardCaptcha();
    }
  },
);
</script>

<style scoped lang="scss">
.physical-second-check-popup {
  overflow: hidden;
  border-radius: 20px 20px 0 0;
  background: #ffffff;
}

.second-check-dialog {
  display: flex;
  flex-direction: column;
  gap: 40px;
  padding: 20px 16px calc(20px + env(safe-area-inset-bottom, 0px));
}

.second-check-dialog__head {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  [data-strong] {
    color: #000000;
    font-size: 18px;
    font-weight: 600;
    line-height: 22px;
  }

  button {
    position: absolute;
    top: 3px;
    right: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    padding: 0;
    border: 0;
    background: transparent;
    color: #000000;
  }
}

.second-check-dialog__form {
  display: flex;
  flex-direction: column;
  gap: 26px;
}

.second-check-section {
  label {
    display: block;
    color: #333333;
    font-size: 14px;
    font-weight: 600;
    line-height: 18px;
  }

  :deep(.van-field) {
    height: 42px;
    padding: 0 16px;
    align-items: center;
    border-radius: 10px;
    background: #f4f5f6;
    line-height: 14px;
  }

  :deep(.van-field__control) {
    color: #333333;
    font-size: 14px;
  }

  :deep(.van-field__control::placeholder) {
    color: #666666;
  }
}

.second-check-captcha-row {
  display: flex;
  gap: 8px;
  margin-top: 8px;

  :deep(.van-field) {
    flex: 1;
    min-width: 0;
  }
}

.second-check-captcha {
  flex: 0 0 113px;
  width: 113px;
  height: 42px;
  overflow: hidden;
  padding: 0;
  border: 1px solid #f4f5f6;
  border-radius: 8px;
  background: #ffffff;

  .app-image {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.second-check-section--sms {
  display: flex;
  flex-direction: column;
  gap: 14px;

  :deep(.van-field) {
    padding-right: 8px;
  }
}

.second-check-section__title {
  display: flex;
  flex-direction: column;
  gap: 4px;

  p {
    margin: 0;
    color: #999999;
    font-size: 12px;
    line-height: 14px;

    em {
      color: var(--wp-color-theme);
      font-style: normal;
    }
  }
}

.second-check-sms-button {
  width: 78px;
  height: 26px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: #d5e5ff;
  color: var(--wp-color-theme);
  font-size: 12px;
  line-height: 12px;

  &:disabled {
    opacity: 0.6;
  }
}

.second-check-dialog__confirm {
  height: 46px;
  margin: 0;
  border: 0;
  background: var(--wp-color-theme) !important;
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;

  &.van-button--disabled {
    background: #f0f0f0 !important;
    color: #999999;
  }
}
</style>
